import {
  Accordion,
  Badge,
  Button,
  Checkbox,
  Group,
  NumberInput,
  Paper,
  Select,
  SimpleGrid,
  Stack,
  Switch,
  Tabs,
  Text,
  TextInput,
  Textarea,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import { api } from '../api';
import { SupplierSectionNav } from '../components/supplier-section-nav';
import { apiHook } from '../treaty';

type SimpleKind = 'status' | 'source' | 'tag' | 'activityType' | 'outcome' | 'unit' | 'category';

export function SupplierSettingsPage() {
  const queryClient = useQueryClient();
  const query = apiHook.useQuery((client) => client.admin['supplier-master-data'].get());
  const data = query.data;
  async function refresh() {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['admin', 'supplier-master-data'] }),
      queryClient.invalidateQueries({ queryKey: ['supplier-master-data'] }),
      queryClient.invalidateQueries({ queryKey: ['product-catalog'] }),
    ]);
  }
  return (
    <Stack gap='lg' maw={1300} mx='auto'>
      <div>
        <Title order={1}>Supplier settings</Title>
        <Text c='dimmed' mt={5}>
          Control workflow, discovery, activity, and product master data.
        </Text>
      </div>
      <SupplierSectionNav />
      {query.isLoading && <Text c='dimmed'>Loading settings…</Text>}
      {data && (
        <Tabs defaultValue='workflow' keepMounted={false}>
          <Tabs.List>
            <Tabs.Tab value='workflow'>Workflow</Tabs.Tab>
            <Tabs.Tab value='activities'>Activities</Tabs.Tab>
            <Tabs.Tab value='catalog'>Product catalog</Tabs.Tab>
          </Tabs.List>
          <Tabs.Panel value='workflow' pt='lg'>
            <SimpleGrid cols={{ base: 1, lg: 3 }}>
              <MasterCard title='Statuses' kind='status' items={data.statuses} refresh={refresh} />
              <MasterCard
                title='Discovery sources'
                kind='source'
                items={data.sourceTypes}
                refresh={refresh}
              />
              <MasterCard title='Tags' kind='tag' items={data.tags} refresh={refresh} />
            </SimpleGrid>
          </Tabs.Panel>
          <Tabs.Panel value='activities' pt='lg'>
            <Stack>
              <MasterCard
                title='Activity types'
                kind='activityType'
                items={data.activityTypes}
                refresh={refresh}
              />
              <SimpleGrid cols={{ base: 1, md: 2 }}>
                {data.activityTypes.map((type) => (
                  <MasterCard
                    key={type.id}
                    title={`${type.name} outcomes`}
                    kind='outcome'
                    activityTypeId={type.id}
                    items={type.outcomes}
                    refresh={refresh}
                  />
                ))}
              </SimpleGrid>
            </Stack>
          </Tabs.Panel>
          <Tabs.Panel value='catalog' pt='lg'>
            <CatalogSettings data={data} refresh={refresh} />
          </Tabs.Panel>
        </Tabs>
      )}
    </Stack>
  );
}

function MasterCard({
  title,
  kind,
  items,
  activityTypeId,
  refresh,
}: {
  title: string;
  kind: SimpleKind;
  items: Array<any>;
  activityTypeId?: string;
  refresh: () => Promise<void>;
}) {
  const [name, setName] = useState('');
  const [color, setColor] = useState('blue');
  const [symbol, setSymbol] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const [countsAsContact, setCountsAsContact] = useState(true);
  const [saving, setSaving] = useState(false);
  async function create() {
    if (!name.trim()) return;
    setSaving(true);
    const response = await api.api.api.v1.admin['supplier-master-records'].post({
      kind,
      name,
      ...(kind === 'status' || kind === 'tag' || kind === 'activityType' || kind === 'outcome'
        ? { color }
        : {}),
      ...(kind === 'unit' ? { symbol } : {}),
      ...(kind === 'status' ? { isDefault } : {}),
      ...(kind === 'activityType' ? { countsAsContact } : {}),
      ...(activityTypeId ? { activityTypeId } : {}),
    });
    setSaving(false);
    if (response.error)
      notifications.show({ color: 'red', message: 'Master record could not be created.' });
    else {
      setName('');
      await refresh();
    }
  }
  async function toggle(item: any) {
    const response = await api.api.api.v1.admin['supplier-master-records']({ id: item.id }).put({
      kind,
      name: item.name,
      active: !item.active,
      ...(item.color ? { color: item.color } : {}),
      ...(typeof item.sortOrder === 'number' ? { sortOrder: item.sortOrder } : {}),
      ...(kind === 'status' ? { isDefault: item.isDefault } : {}),
      ...(kind === 'activityType' ? { countsAsContact: item.countsAsContact } : {}),
      ...(kind === 'unit' ? { symbol: item.symbol } : {}),
    });
    if (!response.error) await refresh();
  }
  return (
    <Paper withBorder radius='lg' p='lg'>
      <Title order={3} mb='md'>
        {title}
      </Title>
      <Stack gap='xs'>
        {items.map((item) => (
          <Group key={item.id} justify='space-between' wrap='nowrap'>
            <Group gap='xs'>
              <Badge variant='light' color={item.color ?? 'blue'}>
                {item.name ?? item.value}
              </Badge>
              {item.isDefault && (
                <Text size='xs' c='dimmed'>
                  Default
                </Text>
              )}
            </Group>
            <Switch checked={item.active} onChange={() => void toggle(item)} />
          </Group>
        ))}
      </Stack>
      <Stack gap='xs' mt='lg'>
        <TextInput
          size='sm'
          label='Add new'
          value={name}
          onChange={(event) => setName(event.currentTarget.value)}
        />
        {kind === 'unit' && (
          <TextInput
            size='sm'
            label='Symbol'
            value={symbol}
            onChange={(event) => setSymbol(event.currentTarget.value)}
          />
        )}
        {['status', 'tag', 'activityType', 'outcome'].includes(kind) && (
          <Select
            size='sm'
            label='Colour'
            data={['blue', 'cyan', 'indigo', 'grape', 'green', 'yellow', 'orange', 'red', 'gray']}
            value={color}
            onChange={(value) => setColor(value ?? 'blue')}
          />
        )}
        {kind === 'status' && (
          <Checkbox
            label='Make default'
            checked={isDefault}
            onChange={(event) => setIsDefault(event.currentTarget.checked)}
          />
        )}
        {kind === 'activityType' && (
          <Checkbox
            label='Counts as contacted'
            checked={countsAsContact}
            onChange={(event) => setCountsAsContact(event.currentTarget.checked)}
          />
        )}
        <Button size='xs' loading={saving} onClick={() => void create()}>
          Add
        </Button>
      </Stack>
    </Paper>
  );
}

function CatalogSettings({ data, refresh }: { data: any; refresh: () => Promise<void> }) {
  return (
    <Stack>
      <SimpleGrid cols={{ base: 1, lg: 2 }}>
        <MasterCard title='Units of measure' kind='unit' items={data.units} refresh={refresh} />
        <CategoryCreator data={data} refresh={refresh} />
      </SimpleGrid>
      <AttributeCreator data={data} refresh={refresh} />
      <ProductCreator data={data} refresh={refresh} />
      <VariantCreator data={data} refresh={refresh} />
      <Accordion variant='separated'>
        {data.products.map((product: any) => (
          <Accordion.Item key={product.id} value={product.id}>
            <Accordion.Control>
              <Group>
                <Text fw={600}>{[product.brand, product.name].filter(Boolean).join(' ')}</Text>
                <Badge variant='light'>
                  {
                    data.categories.find((category: any) => category.id === product.categoryId)
                      ?.name
                  }
                </Badge>
              </Group>
            </Accordion.Control>
            <Accordion.Panel>
              <Stack>
                {data.variants
                  .filter((variant: any) => variant.productId === product.id)
                  .map((variant: any) => (
                    <Paper key={variant.id} withBorder p='sm'>
                      <Text fw={600}>{variant.name}</Text>
                      <Text size='xs' c='dimmed'>
                        {variant.sku || 'No SKU'} · {variant.defaultUnit.symbol}
                      </Text>
                    </Paper>
                  ))}
                {!data.variants.some((variant: any) => variant.productId === product.id) && (
                  <Text size='sm' c='dimmed'>
                    No variants.
                  </Text>
                )}
              </Stack>
            </Accordion.Panel>
          </Accordion.Item>
        ))}
      </Accordion>
    </Stack>
  );
}

function CategoryCreator({ data, refresh }: { data: any; refresh: () => Promise<void> }) {
  const [name, setName] = useState('');
  const [parentId, setParentId] = useState<string | null>(null);
  async function create() {
    const response = await api.api.api.v1.admin['supplier-master-records'].post({
      kind: 'category',
      name,
      ...(parentId ? { parentId } : {}),
    });
    if (!response.error) {
      setName('');
      setParentId(null);
      await refresh();
    }
  }
  return (
    <Paper withBorder radius='lg' p='lg'>
      <Title order={3}>Categories</Title>
      <Stack mt='md'>
        {data.categories.map((category: any) => (
          <Group key={category.id} justify='space-between'>
            <Text size='sm'>{category.name}</Text>
            <Badge variant='outline'>
              {data.categories.find((item: any) => item.id === category.parentId)?.name ?? 'Root'}
            </Badge>
          </Group>
        ))}
        <TextInput
          label='Category name'
          value={name}
          onChange={(e) => setName(e.currentTarget.value)}
        />
        <Select
          clearable
          searchable
          label='Parent category'
          data={data.categories.map((item: any) => ({ value: item.id, label: item.name }))}
          value={parentId}
          onChange={setParentId}
        />
        <Button size='xs' onClick={() => void create()}>
          Add category
        </Button>
      </Stack>
    </Paper>
  );
}

function AttributeCreator({ data, refresh }: { data: any; refresh: () => Promise<void> }) {
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [dataType, setDataType] = useState<string | null>('select');
  const [unitId, setUnitId] = useState<string | null>(null);
  const [required, setRequired] = useState(true);
  const [optionAttributeId, setOptionAttributeId] = useState<string | null>(null);
  const [option, setOption] = useState('');
  async function createAttribute() {
    if (!categoryId || !dataType) return;
    const response = await api.api.api.v1.admin['product-attributes'].post({
      categoryId,
      name,
      dataType: dataType as 'text' | 'number' | 'boolean' | 'select',
      ...(unitId ? { unitId } : {}),
      required,
    });
    if (!response.error) {
      setName('');
      await refresh();
    }
  }
  async function createOption() {
    if (!optionAttributeId) return;
    const response = await api.api.api.v1.admin['product-attribute-options'].post({
      attributeId: optionAttributeId,
      value: option,
    });
    if (!response.error) {
      setOption('');
      await refresh();
    }
  }
  return (
    <Paper withBorder radius='lg' p='lg'>
      <Title order={3}>Category attributes</Title>
      <SimpleGrid cols={{ base: 1, md: 2 }} mt='md'>
        <Stack>
          <Select
            required
            searchable
            label='Category'
            data={data.categories.map((item: any) => ({ value: item.id, label: item.name }))}
            value={categoryId}
            onChange={setCategoryId}
          />
          <TextInput
            required
            label='Attribute name'
            placeholder='Thickness, grade, size…'
            value={name}
            onChange={(e) => setName(e.currentTarget.value)}
          />
          <Select
            label='Type'
            data={[
              ['select', 'Select'],
              ['number', 'Number'],
              ['text', 'Text'],
              ['boolean', 'Yes / no'],
            ].map(([value, label]) => ({ value, label }))}
            value={dataType}
            onChange={setDataType}
          />
          {dataType === 'number' && (
            <Select
              clearable
              searchable
              label='Unit'
              data={data.units.map((item: any) => ({ value: item.id, label: item.name }))}
              value={unitId}
              onChange={setUnitId}
            />
          )}
          <Checkbox
            label='Required for variants'
            checked={required}
            onChange={(e) => setRequired(e.currentTarget.checked)}
          />
          <Button size='xs' onClick={() => void createAttribute()}>
            Add attribute
          </Button>
        </Stack>
        <Stack>
          <Select
            required
            searchable
            label='Select attribute'
            data={data.attributes
              .filter((item: any) => item.dataType === 'select')
              .map((item: any) => ({
                value: item.id,
                label: `${data.categories.find((category: any) => category.id === item.categoryId)?.name}: ${item.name}`,
              }))}
            value={optionAttributeId}
            onChange={setOptionAttributeId}
          />
          <TextInput
            label='Allowed value'
            value={option}
            onChange={(e) => setOption(e.currentTarget.value)}
          />
          <Button size='xs' variant='light' onClick={() => void createOption()}>
            Add option
          </Button>
          <Stack gap={4}>
            {data.attributes.map((attribute: any) => (
              <Text key={attribute.id} size='xs'>
                <b>{attribute.name}</b> ({attribute.dataType}) —{' '}
                {data.options
                  .filter((item: any) => item.attributeId === attribute.id)
                  .map((item: any) => item.value)
                  .join(', ') || 'no options'}
              </Text>
            ))}
          </Stack>
        </Stack>
      </SimpleGrid>
    </Paper>
  );
}

function ProductCreator({ data, refresh }: { data: any; refresh: () => Promise<void> }) {
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [brand, setBrand] = useState('');
  const [description, setDescription] = useState('');
  async function create() {
    if (!categoryId) return;
    const response = await api.api.api.v1.admin.products.post({
      categoryId,
      name,
      ...(brand ? { brand } : {}),
      ...(description ? { description } : {}),
    });
    if (!response.error) {
      setName('');
      setBrand('');
      setDescription('');
      await refresh();
    }
  }
  return (
    <Paper withBorder radius='lg' p='lg'>
      <Title order={3}>Product families</Title>
      <SimpleGrid cols={{ base: 1, md: 4 }} mt='md'>
        <Select
          required
          searchable
          label='Category'
          data={data.categories.map((item: any) => ({ value: item.id, label: item.name }))}
          value={categoryId}
          onChange={setCategoryId}
        />
        <TextInput
          required
          label='Product name'
          value={name}
          onChange={(e) => setName(e.currentTarget.value)}
        />
        <TextInput label='Brand' value={brand} onChange={(e) => setBrand(e.currentTarget.value)} />
        <Textarea
          label='Description'
          autosize
          minRows={1}
          value={description}
          onChange={(e) => setDescription(e.currentTarget.value)}
        />
      </SimpleGrid>
      <Group justify='flex-end' mt='md'>
        <Button size='xs' onClick={() => void create()}>
          Add product
        </Button>
      </Group>
    </Paper>
  );
}

function VariantCreator({ data, refresh }: { data: any; refresh: () => Promise<void> }) {
  const [productId, setProductId] = useState<string | null>(null);
  const [sku, setSku] = useState('');
  const [unitId, setUnitId] = useState<string | null>(null);
  const [values, setValues] = useState<Record<string, string | number | boolean>>({});
  const product = data.products.find((item: any) => item.id === productId);
  const attributes = useMemo(() => {
    if (!product) return [];
    const categoryIds: string[] = [];
    let categoryId: string | null = product.categoryId;
    while (categoryId) {
      categoryIds.unshift(categoryId);
      categoryId = data.categories.find((item: any) => item.id === categoryId)?.parentId ?? null;
    }
    return data.attributes
      .filter((attribute: any) => categoryIds.includes(attribute.categoryId) && attribute.active)
      .sort((a: any, b: any) => a.sortOrder - b.sortOrder);
  }, [data, product]);
  async function create() {
    if (!productId || !unitId) return;
    const payload = attributes
      .filter((attribute: any) => values[attribute.id] !== undefined && values[attribute.id] !== '')
      .map((attribute: any) => ({
        attributeId: attribute.id,
        ...(attribute.dataType === 'select'
          ? { optionId: String(values[attribute.id]) }
          : attribute.dataType === 'number'
            ? { numberValue: Number(values[attribute.id]) }
            : attribute.dataType === 'boolean'
              ? { booleanValue: Boolean(values[attribute.id]) }
              : { textValue: String(values[attribute.id]) }),
      }));
    const response = await api.api.api.v1.admin['product-variants'].post({
      productId,
      defaultUnitId: unitId,
      ...(sku ? { sku } : {}),
      values: payload,
    });
    if (response.error)
      notifications.show({
        color: 'red',
        message: 'Variant could not be created. Check required and duplicate values.',
      });
    else {
      setSku('');
      setValues({});
      await refresh();
    }
  }
  return (
    <Paper withBorder radius='lg' p='lg'>
      <Title order={3}>Manual variants</Title>
      <SimpleGrid cols={{ base: 1, md: 3 }} mt='md'>
        <Select
          required
          searchable
          label='Product'
          data={data.products.map((item: any) => ({
            value: item.id,
            label: [item.brand, item.name].filter(Boolean).join(' '),
          }))}
          value={productId}
          onChange={(value) => {
            setProductId(value);
            setValues({});
          }}
        />
        <TextInput
          label='Internal SKU'
          value={sku}
          onChange={(e) => setSku(e.currentTarget.value)}
        />
        <Select
          required
          searchable
          label='Default purchasing unit'
          data={data.units.map((item: any) => ({
            value: item.id,
            label: `${item.name} (${item.symbol})`,
          }))}
          value={unitId}
          onChange={setUnitId}
        />
      </SimpleGrid>
      <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} mt='md'>
        {attributes.map((attribute: any) =>
          attribute.dataType === 'select' ? (
            <Select
              key={attribute.id}
              required={attribute.required}
              label={attribute.name}
              data={data.options
                .filter((option: any) => option.attributeId === attribute.id && option.active)
                .map((option: any) => ({ value: option.id, label: option.value }))}
              value={String(values[attribute.id] ?? '') || null}
              onChange={(value) =>
                setValues((current) => ({ ...current, [attribute.id]: value ?? '' }))
              }
            />
          ) : attribute.dataType === 'number' ? (
            <NumberInput
              key={attribute.id}
              required={attribute.required}
              label={attribute.name}
              value={(values[attribute.id] as number) ?? ''}
              onChange={(value) => setValues((current) => ({ ...current, [attribute.id]: value }))}
            />
          ) : attribute.dataType === 'boolean' ? (
            <Checkbox
              key={attribute.id}
              mt='xl'
              label={attribute.name}
              checked={Boolean(values[attribute.id])}
              onChange={(e) =>
                setValues((current) => ({ ...current, [attribute.id]: e.currentTarget.checked }))
              }
            />
          ) : (
            <TextInput
              key={attribute.id}
              required={attribute.required}
              label={attribute.name}
              value={String(values[attribute.id] ?? '')}
              onChange={(e) =>
                setValues((current) => ({ ...current, [attribute.id]: e.currentTarget.value }))
              }
            />
          ),
        )}
      </SimpleGrid>
      <Group justify='flex-end' mt='md'>
        <Button size='xs' onClick={() => void create()}>
          Create variant
        </Button>
      </Group>
    </Paper>
  );
}
